<template>
  <StandardButtonWithMenu
    :button-props="resolvedButtonProps"
    :menu-props="menuProps"
  />
</template>

<script lang="ts">
/**
 * As of March 2024, all users should be able to download a graph as PNG or copy to clipboard.
 * Please note that copy to clipboard is NOT supported in Firefox.
 * Furthermore, the download Excel data option will only ever be shown to admins.
 */
export default {
  name: 'ExportGraphButton',
  compatConfig: { MODE: 3 },
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import type { MenuItem } from 'primevue/menuitem';
import useTranslation from '@/composables/useTranslation';
import { useFeatureFlag } from '@/composables/useFeatureFlag';
import { UserPermission } from '@/constants/UserPermission';
import { useHasPermission } from '@/composables/usePermission';
import { useRoute } from 'vue-router';
import useRouteChecks from '@/composables/useRouteChecks';
import { ButtonSize, ButtonVariant, type StandardButtonProps } from '@/types/StandardButtonTypes';
import type { StandardMenuProps } from '@/components/standard-components/StandardMenu.vue';

type ExportGraphMenuItem = MenuItem & {
  iconClass?: string;
};

const props = withDefaults(
  defineProps<{
    /**
     * Merged into the export trigger `StandardButton` props after component defaults.
     * `disabled`, `loading` (from `isExportingGraph`), and `size` (from `size`) still win.
     */
    buttonProps?: Partial<StandardButtonProps>;
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    isExportingGraph?: boolean;
    /**
     * Excel export must be implemented for each graph individually because the data for each chart varies.
     * If it is implemented, set this prop to true so that admins may export Excel from the chart as desired.
     */
    isExcelExportImplemented?: boolean;
    /**
     * isCopyToClipboardImplemented should only be false when you have graphs with the following:
     * Graph using the amChart v4 library as current export function
     *
     * Copy to clipboard only supports v5 graphs.
     */
    isCopyToClipboardImplemented?: boolean;
  }>(),
  {
    size: 'sm',
    disabled: false,
    isExportingGraph: false,
    isExcelExportImplemented: false,
    isCopyToClipboardImplemented: true,
  },
);

const emit = defineEmits<{
  download: [format?: 'png' | 'xlsx'];
  copy: [];
}>();

const { translate } = useTranslation();
const route = useRoute();
const { isOnPortfolioConstruction } = useRouteChecks(route);
const { isCopyToClipboardSupported } = useFeatureFlag();

const shouldShowCopyToClipboardButton = computed(
  () => props.isCopyToClipboardImplemented && isCopyToClipboardSupported.value,
);

const excelPermission = computed((): UserPermission | null => {
  if (isOnPortfolioConstruction.value) {
    return UserPermission.PORTFOLIO_EXCEL;
  }

  return null;
});

const showExcelButton = useHasPermission(excelPermission);

const buttonSize = computed((): ButtonSize => {
  if (props.size === 'lg') {
    return ButtonSize.LARGE;
  }

  if (props.size === 'md') {
    return ButtonSize.NORMAL;
  }

  return ButtonSize.SMALL;
});

const downloadLabel = computed((): string => translate({ path: 'GLOBAL.DOWNLOAD' }));

const mergeExportGraphButtonClassProp = (
  parentClassProp?: StandardButtonProps['classProp'],
): StandardButtonProps['classProp'] => {
  if (parentClassProp == null) {
    return 'd-print-none';
  }

  if (typeof parentClassProp === 'string') {
    return `d-print-none ${parentClassProp}`;
  }

  if (Array.isArray(parentClassProp)) {
    return ['d-print-none', ...parentClassProp];
  }

  return parentClassProp;
};

const resolvedButtonProps = computed((): StandardButtonProps => {
  const { classProp: parentClassProp, ...parentButtonProps } = props.buttonProps ?? {};

  return {
    dataTestid: 'export-graph',
    icon: 'arrow-down-to-bracket',
    label: downloadLabel.value,
    variant: ButtonVariant.OUTLINED,
    ...parentButtonProps,
    size: buttonSize.value,
    loading: props.isExportingGraph,
    disabled: props.disabled,
    classProp: mergeExportGraphButtonClassProp(parentClassProp),
  };
});

const menuItems = computed((): ExportGraphMenuItem[] => {
  const items: ExportGraphMenuItem[] = [
    {
      label: downloadLabel.value,
      icon: 'file-image',
      iconClass: 'text-danger',
      command: () => {
        emit('download', 'png');
      },
    },
  ];

  if (shouldShowCopyToClipboardButton.value) {
    items.push({
      label: translate({ path: 'GLOBAL.COPY' }),
      icon: 'copy',
      iconClass: 'text-info',
      command: () => {
        emit('copy');
      },
    });
  }

  if (props.isExcelExportImplemented && showExcelButton.value) {
    items.push({
      label: translate({ path: 'GLOBAL.EXCEL' }),
      icon: 'file-excel',
      iconClass: 'text-success',
      command: () => {
        emit('download', 'xlsx');
      },
    });
  }

  return items;
});

const menuProps = computed(
  (): StandardMenuProps => ({
    menuItems: menuItems.value,
    dropdownAlign: 'end',
    contentClass: 'shadow',
  }),
);
</script>
